import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { CalendarCheck, Save, CheckCheck } from 'lucide-react';
import { useT } from '../../i18n/LanguageContext';
import { useOrgId } from '../../hooks/useOrgId';
import { useAcademicYear } from '../../context/AcademicYearContext';
import { schoolApi } from '../../api/school';
import { PageHeader, EmptyState, InlineSpinner, Card } from '../../components/school/ui';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';

const STATUS_META = [
  { key: 'present', tKey: 'att.present', active: 'bg-emerald-600 text-white border-emerald-600 hover:bg-emerald-600' },
  { key: 'late', tKey: 'att.late', active: 'bg-amber-500 text-white border-amber-500 hover:bg-amber-500' },
  { key: 'excused', tKey: 'att.excused', active: 'bg-sky-600 text-white border-sky-600 hover:bg-sky-600' },
  { key: 'absent', tKey: 'att.absent', active: 'bg-rose-600 text-white border-rose-600 hover:bg-rose-600' },
];

const todayISO = () => new Date().toISOString().slice(0, 10);

export default function AttendancePage() {
  const { t } = useT();
  const orgId = useOrgId();
  const { selectedYearId } = useAcademicYear();
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [classId, setClassId] = useState('');
  const [sectionId, setSectionId] = useState('');
  const [date, setDate] = useState(todayISO());
  const [rows, setRows] = useState([]);
  const [taken, setTaken] = useState(false);
  const [loadingGrid, setLoadingGrid] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!orgId) return;
    schoolApi.listClasses(orgId, selectedYearId).then(setClasses).catch(() => {});
    setClassId(''); setSectionId(''); setRows([]);
  }, [orgId, selectedYearId]);

  useEffect(() => {
    if (classId) {
      schoolApi.listSections(orgId, classId, selectedYearId).then(setSections).catch(() => setSections([]));
    } else {
      setSections([]);
    }
    setSectionId('');
  }, [orgId, classId, selectedYearId]);

  const loadGrid = useCallback(async () => {
    if (!orgId || !classId || !sectionId || !date) { setRows([]); return; }
    setLoadingGrid(true);
    try {
      const data = await schoolApi.getAttendance(orgId, { class_id: classId, section_id: sectionId, date, academic_year_id: selectedYearId });
      setTaken(!!data.taken);
      setRows(data.students.map((s) => ({
        student_id: s.student_id,
        name: s.name,
        roll_no: s.roll_no,
        admission_no: s.admission_no,
        status: s.status || 'present',
        note: s.note || '',
      })));
    } catch (e) {
      toast.error('Failed to load attendance');
    } finally {
      setLoadingGrid(false);
    }
  }, [orgId, classId, sectionId, date]);

  useEffect(() => { loadGrid(); }, [loadGrid]);

  const setStatus = (i, status) => setRows((r) => { const n = [...r]; n[i] = { ...n[i], status }; return n; });
  const markAllPresent = () => setRows((r) => r.map((x) => ({ ...x, status: 'present' })));

  const summary = useMemo(() => {
    const c = { present: 0, late: 0, excused: 0, absent: 0 };
    rows.forEach((x) => { if (c[x.status] !== undefined) c[x.status] += 1; });
    const total = rows.length;
    const rate = total ? Math.round(((c.present + c.late) / total) * 1000) / 10 : 0;
    return { ...c, total, rate };
  }, [rows]);

  const save = async () => {
    if (!rows.length) return;
    setSaving(true);
    try {
      await schoolApi.saveAttendance({
        organization_id: orgId,
        date,
        class_id: classId,
        section_id: sectionId,
        entries: rows.map((r) => ({ student_id: r.student_id, status: r.status, note: r.note || null })),
      });
      toast.success(t('att.saved'));
      loadGrid();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save attendance');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-[1400px] mx-auto">
      <PageHeader title={t('att.title')} subtitle={t('att.takeSubtitle')} icon={CalendarCheck} />

      <Card className="p-4 mb-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <Select value={classId} onValueChange={setClassId}>
            <SelectTrigger data-testid="att-class-select"><SelectValue placeholder={t('students.class')} /></SelectTrigger>
            <SelectContent>{classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={sectionId} onValueChange={setSectionId} disabled={!classId}>
            <SelectTrigger data-testid="att-section-select"><SelectValue placeholder={t('students.section')} /></SelectTrigger>
            <SelectContent>{sections.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
          </Select>
          <Input type="date" value={date} max={todayISO()} onChange={(e) => setDate(e.target.value)} data-testid="att-date-input" aria-label={t('att.date')} />
        </div>
      </Card>

      {/* Summary chips */}
      {rows.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-4" data-testid="att-summary">
          <SummaryChip label={t('att.present')} value={summary.present} color="text-emerald-600" />
          <SummaryChip label={t('att.late')} value={summary.late} color="text-amber-600" />
          <SummaryChip label={t('att.excused')} value={summary.excused} color="text-sky-600" />
          <SummaryChip label={t('att.absent')} value={summary.absent} color="text-rose-600" />
          <SummaryChip label={t('att.rate')} value={`${summary.rate}%`} color="text-primary" testId="att-rate" />
        </div>
      )}

      <Card>
        {loadingGrid ? <InlineSpinner /> : (!classId || !sectionId) ? (
          <EmptyState icon={CalendarCheck} title={t('att.selectFilters')} description={t('att.selectFiltersDesc')} testId="att-empty-filters" />
        ) : rows.length === 0 ? (
          <EmptyState icon={CalendarCheck} title={t('common.noData')} description={t('att.noStudents')} testId="att-empty-students" />
        ) : (
          <>
            <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-border">
              <div className="text-sm text-muted-foreground">
                {taken && <span className="text-amber-700" data-testid="att-already-taken">{t('att.alreadyTaken')}</span>}
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" onClick={markAllPresent} className="gap-2" data-testid="att-mark-all-present">
                  <CheckCheck className="w-4 h-4" /> {t('att.markAllPresent')}
                </Button>
                <Button onClick={save} disabled={saving} className="gap-2" data-testid="att-save-button">
                  <Save className="w-4 h-4" /> {saving ? t('common.saving') : t('common.save')}
                </Button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                    <th className="px-4 py-3 w-20">{t('students.rollNo')}</th>
                    <th className="px-4 py-3">{t('common.name')}</th>
                    <th className="px-4 py-3 w-[420px]">{t('common.status')}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={r.student_id} className="border-b border-border last:border-0" data-testid={`att-row-${r.student_id}`}>
                      <td className="px-4 py-2 tabular-nums">{r.roll_no || '-'}</td>
                      <td className="px-4 py-2 font-medium">{r.name}</td>
                      <td className="px-4 py-2">
                        <div className="inline-flex rounded-md border border-border overflow-hidden">
                          {STATUS_META.map((m) => (
                            <button
                              key={m.key}
                              type="button"
                              onClick={() => setStatus(i, m.key)}
                              className={`px-3 py-1.5 text-xs font-medium border-r border-border last:border-r-0 transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${r.status === m.key ? m.active : 'bg-transparent text-muted-foreground hover:bg-muted'}`}
                              data-testid={`att-${m.key}-${r.student_id}`}
                              aria-pressed={r.status === m.key}
                            >
                              {t(m.tKey)}
                            </button>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}

const SummaryChip = ({ label, value, color, testId }) => (
  <div className="bg-card border border-border rounded-md px-4 py-3" data-testid={testId}>
    <p className="text-xs text-muted-foreground uppercase tracking-wider">{label}</p>
    <p className={`text-2xl font-semibold tabular-nums mt-1 ${color}`}>{value}</p>
  </div>
);
